import { setProvider, setAnalyticsDebug, registerSuperProperties } from "common/analytics";
import { gaMeasurementId } from "common/config";
import { webMixpanelProvider } from "./mixpanelProvider";
import { initGa4 } from "./ga4";
import { registerPermissionSuperProps } from "./superProps";
import { resolveDeviceFormFactor } from "./device";

// Guard so this can be safely called from BOTH the client entry and __root (idempotent).
let analyticsInitialized = false;

/**
 * Wires the new Mixpanel pipeline on the client. Replaces the disabled legacy chain
 * (mixpanel-client.ts -> services/analytics.ts). Mixpanel and GA4 are each switched on by their
 * own setting; with neither set it stays unconfigured (silent no-op).
 */
export function initWebAnalytics(): void {
  if (typeof window === "undefined") return; // SSR guard
  if (analyticsInitialized) return; // idempotent: safe from entry and/or __root
  analyticsInitialized = true;

  const token = import.meta.env.VITE_MIXPANEL_TOKEN_V2 ?? "";
  const gaId = gaMeasurementId || "";
  if (!token && !gaId) return; // nothing configured -> do not register a provider

  if (token) {
    // A Mixpanel init failure must not stop GA4 or the provider; Mixpanel then stays off for the session.
    try {
      webMixpanelProvider.init(token, {
        api_host: import.meta.env.VITE_MIXPANEL_HOST || undefined, // EU data-residency project
        track_pageview: false,
        persistence: "localStorage",
        debug: import.meta.env.DEV, // local-only: logs every mixpanel.track() to the console
      });
    } catch (err) {
      if (import.meta.env.DEV) console.warn("[analytics] Mixpanel init failed", err);
    }
  }
  if (gaId) initGa4(gaId);
  setProvider(webMixpanelProvider);
  setAnalyticsDebug(import.meta.env.DEV);

  // Baseline super properties. product_type discriminates the consumer web app from the business
  // portal / widget (which share generated event names); device is the viewport form factor
  // (apps/web is responsive). dark_mode/auth_state are registered reactively by
  // useAnalyticsSuperProperties (RootLayout), which also keeps device live; page_name on route change.
  // (user_type is now a people property, also set there via setUserProperties — no longer a super prop.)
  registerSuperProperties({
    product_type: "app",
    device: resolveDeviceFormFactor(),
  });
  void registerPermissionSuperProps(); // async browser permission query (location + notification)
}
